<template>
  <v-app>
    <v-main
      class="auth-pages page-layout-main"
      :class="{
        'page-layout-main--cover': isCoverLanding,
        'page-layout-main--product': isProductLanding,
      }"
      :style="coverThemeStyle"
    >
      <div
        v-if="!$route.meta.hideHeader"
        class="header-auth position-relative border-radius-xl"
        :class="headerClasses"
      >
        <div v-if="isLanding" class="public-language-switcher">
          <v-icon size="14">fas fa-globe-americas</v-icon>
          <select
            v-model="selectedLocale"
            :aria-label="$t('publicPages.common.languageSelector')"
          >
            <option
              v-for="language in languages"
              :key="language.item"
              :value="language.item"
            >
              {{ language.description }}
            </option>
          </select>
        </div>
        <v-img
          src="@/assets/img/shapes/waves-white.svg"
          alt="pattern-lines"
          class="position-absolute opacity-6 start-0 top-0 w-100"
          v-if="
            this.$route.name == 'Landing' &&
            !isCoverLanding &&
            !isProductLanding
          "
        ></v-img>
        <v-container
          v-if="isProductLanding"
          class="cajupe-product-hero-container"
        >
          <v-row class="cajupe-product-hero-row">
            <v-col cols="12" md="5" class="cajupe-product-hero-content">
              <div class="cajupe-product-brand" aria-label="Cajupe">
                <img :src="logoSrc" alt="" />
                <span>Cajupe<sup>®</sup></span>
              </div>

              <div class="cajupe-product-copy">
                <span class="cajupe-product-eyebrow">
                  {{ localizedBrand.landingEyebrow }}
                </span>
                <h1>
                  {{ localizedBrand.landingPromoTitle }}
                  <span>{{ localizedBrand.landingPromoHighlight }}</span>
                </h1>
                <p>{{ localizedBrand.landingPromoText }}</p>
              </div>

              <div class="cajupe-product-access">
                <span>
                  <i></i>{{ $t("publicPages.common.quickSecureAccess") }}
                </span>
                <v-btn
                  elevation="0"
                  height="62"
                  :to="landingLoginRoute"
                  depressed
                  link
                  class="cajupe-product-cta white--text"
                  :style="landingButtonStyle"
                >
                  {{ landingButtonLabel }}
                  <span class="cajupe-product-cta-icon">
                    <v-icon size="15">fas fa-arrow-right</v-icon>
                  </span>
                </v-btn>
              </div>
            </v-col>

            <v-col cols="12" md="7" class="cajupe-product-hero-visual">
              <div class="cajupe-product-window" aria-hidden="true">
                <div class="cajupe-product-window-header">
                  <div class="cajupe-product-window-dots">
                    <span></span><span></span><span></span>
                  </div>
                  <strong>{{
                    $t("publicPages.common.operationsCenter")
                  }}</strong>
                  <span class="cajupe-product-status">
                    {{ $t("publicPages.common.online") }}
                  </span>
                </div>

                <div class="cajupe-product-window-body">
                  <div class="cajupe-product-welcome">
                    <div>
                      <small>{{ $t("publicPages.common.overview") }}</small>
                      <h2>
                        {{ $t("publicPages.common.operationInOnePlace") }}
                      </h2>
                    </div>
                    <span>{{ $t("publicPages.common.today") }}</span>
                  </div>

                  <div class="cajupe-product-metrics">
                    <div>
                      <span class="cajupe-product-metric-icon">
                        <v-icon size="17">fas fa-users</v-icon>
                      </span>
                      <small>{{ $t("publicPages.common.team") }}</small>
                      <strong>{{ $t("publicPages.common.connected") }}</strong>
                    </div>
                    <div>
                      <span class="cajupe-product-metric-icon">
                        <v-icon size="17">fas fa-fingerprint</v-icon>
                      </span>
                      <small>{{
                        $t("publicPages.common.workSchedules")
                      }}</small>
                      <strong>{{ $t("publicPages.common.upToDate") }}</strong>
                    </div>
                    <div>
                      <span class="cajupe-product-metric-icon">
                        <v-icon size="17">fas fa-check</v-icon>
                      </span>
                      <small>{{ $t("publicPages.common.tasks") }}</small>
                      <strong>{{ $t("publicPages.common.activeFlow") }}</strong>
                    </div>
                  </div>

                  <div class="cajupe-product-flow">
                    <div class="cajupe-product-flow-heading">
                      <div>
                        <small>{{
                          $t("publicPages.common.recentActivity")
                        }}</small>
                        <strong>{{
                          $t("publicPages.common.realTimeMonitoring")
                        }}</strong>
                      </div>
                      <span>{{ $t("publicPages.common.updatedNow") }}</span>
                    </div>
                    <div class="cajupe-product-flow-row">
                      <span><v-icon size="14">fas fa-clock</v-icon></span>
                      <p>
                        <strong>{{
                          $t("publicPages.common.workScheduleProcessed")
                        }}</strong
                        >{{ $t("publicPages.common.recordUpdated") }}
                      </p>
                      <em>{{ $t("publicPages.common.now") }}</em>
                    </div>
                    <div class="cajupe-product-flow-row">
                      <span><v-icon size="14">fas fa-file-alt</v-icon></span>
                      <p>
                        <strong>{{
                          $t("publicPages.common.documentReceived")
                        }}</strong
                        >{{ $t("publicPages.common.fileOrganized") }}
                      </p>
                      <em>{{ $t("publicPages.common.fiveMinutesAgo") }}</em>
                    </div>
                    <div class="cajupe-product-flow-row">
                      <span><v-icon size="14">fas fa-tasks</v-icon></span>
                      <p>
                        <strong>{{
                          $t("publicPages.common.taskCompleted")
                        }}</strong
                        >{{ $t("publicPages.common.flowSynchronized") }}
                      </p>
                      <em>{{ $t("publicPages.common.twelveMinutesAgo") }}</em>
                    </div>
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-container>

        <v-container v-else :class="headerContainerClass">
          <v-row class="d-flex" :class="headerRowClass">
            <v-col
              cols="12"
              :md="isCoverLanding ? 5 : 6"
              class="py-0"
              :class="[heroContentColumnClass, { 'mx-auto': !isCoverLanding }]"
            >
              <div :class="heroContentClass">
                <div class="logo-box" :class="logoBoxClass">
                  <img :src="logoSrc" :class="logoClass" :alt="logoAlt" />
                </div>

                <div
                  v-if="
                    usesInlinePromoLanding && localizedBrand.landingPromoTitle
                  "
                  class="page-layout-hero-immersive-promo"
                >
                  <span v-if="localizedBrand.landingEyebrow">
                    {{ localizedBrand.landingEyebrow }}
                  </span>
                  <h3>{{ localizedBrand.landingPromoTitle }}</h3>
                  <p v-if="localizedBrand.landingPromoText">
                    {{ localizedBrand.landingPromoText }}
                  </p>
                </div>

                <div :class="heroCopyClass">
                  <span
                    v-if="isCoverLanding"
                    class="page-layout-hero-copy-accent"
                    aria-hidden="true"
                  ></span>
                  <h2
                    class="text-h3 font-weight-bold text-center mb-2"
                    :class="landingTitleClass"
                    v-if="this.$route.name == 'Landing' && headerTitleText"
                  >
                    {{ headerTitleText }}
                  </h2>
                  <h1
                    class="text-h1 font-weight-bolder text-center mb-2"
                    :class="[titleClass, titleColorClass]"
                    v-else
                  >
                    {{ headerTitleText }}
                  </h1>
                  <p
                    v-if="headerParagraphText"
                    class="
                      text-h2
                      font-weight-bolder font-size-root
                      text-center
                      font-weight-thin
                      mb-12
                    "
                    :class="landingParagraphClass"
                  >
                    {{ headerParagraphText }}
                  </p>
                </div>

                <v-btn
                  v-if="this.$route.name == 'Landing'"
                  elevation="0"
                  height="45"
                  :to="landingLoginRoute"
                  depressed
                  link
                  class="font-weight-bold text-uppercase text- py-2 px-5 w-100"
                  :class="landingButtonClass"
                  :style="landingButtonStyle"
                >
                  {{ landingButtonLabel }}
                  <v-icon right size="16">fas fa-arrow-right</v-icon>
                </v-btn>
              </div>
            </v-col>
            <v-col
              v-if="isCoverLanding"
              cols="12"
              md="7"
              class="pa-0 page-layout-hero-media"
              :class="{
                'page-layout-hero-media--immersive': isImmersiveLanding,
                'page-layout-hero-media--showcase': isShowcaseLanding,
              }"
            >
              <img
                :src="whiteLabelBrand.landingCover"
                :alt="localizedBrand.landingCoverAlt"
                class="page-layout-hero-cover"
              />
              <div
                v-if="
                  !usesInlinePromoLanding && localizedBrand.landingPromoTitle
                "
                class="page-layout-hero-media-copy"
              >
                <span v-if="localizedBrand.landingEyebrow">
                  {{ localizedBrand.landingEyebrow }}
                </span>
                <h3>{{ localizedBrand.landingPromoTitle }}</h3>
                <p v-if="localizedBrand.landingPromoText">
                  {{ localizedBrand.landingPromoText }}
                </p>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </div>
      <!-- <app-bar-auth background="transparent" has-bg linkColor="white">
      </app-bar-auth> -->
      <Transition origin="center top" mode="out-in">
        <!-- your content here -->
        <v-container :class="contentContainerClass">
          <router-view></router-view>
          <content-footer
            auth
            :white-label-brand="whiteLabelBrand"
            v-if="!$route.meta.hideFooter"
          ></content-footer>
        </v-container>
      </Transition>
    </v-main>
  </v-app>
</template>
<script>
import ContentFooter from "@/components/Footer.vue";
import { LANGUAGE_OPTIONS, saveLocale } from "@/utils/locale";
import {
  getLoginRouteByBrand,
  getLocalizedWhiteLabelText,
  isClientWhiteLabel,
  resolveWhiteLabelBrand,
} from "@/utils/whiteLabel";

export default {
  name: "page-layout",
  components: {
    ContentFooter,
  },
  data() {
    return {
      tab: null,
      publicHeaderImage: null,
      publicHeaderAlt: null,
      languages: LANGUAGE_OPTIONS,
    };
  },
  computed: {
    isLanding() {
      return this.$route.name === "Landing";
    },
    isPreContratacaoPublica() {
      return this.$route.name === "PreContratacaoPublica";
    },
    isRegistroPontoWebPublico() {
      return this.$route.name === "RegistroPontoWebPublico";
    },
    usesCompactHeader() {
      return this.isPreContratacaoPublica || this.isRegistroPontoWebPublico;
    },
    whiteLabelBrand() {
      return resolveWhiteLabelBrand();
    },
    localizedBrand() {
      return [
        "landingSubtitle",
        "landingEyebrow",
        "landingPromoTitle",
        "landingPromoHighlight",
        "landingPromoText",
        "landingButtonLabel",
        "landingCoverAlt",
      ].reduce((content, field) => {
        content[field] = getLocalizedWhiteLabelText(
          this.whiteLabelBrand,
          field,
          this.$i18n
        );
        return content;
      }, {});
    },
    selectedLocale: {
      get() {
        return this.$i18n.locale;
      },
      set(locale) {
        this.$i18n.locale = saveLocale(locale);
      },
    },
    isWhiteLabelClient() {
      return isClientWhiteLabel(this.whiteLabelBrand);
    },
    isProductLanding() {
      return (
        this.isLanding &&
        this.whiteLabelBrand.landingVariant === "product-suite"
      );
    },
    isImmersiveLanding() {
      return (
        this.isLanding &&
        this.whiteLabelBrand.landingVariant === "immersive-cover" &&
        Boolean(this.whiteLabelBrand.landingCover)
      );
    },
    isDarkImmersiveLanding() {
      return (
        this.isImmersiveLanding &&
        this.whiteLabelBrand.landingPanelVariant === "dark-legacy"
      );
    },
    isShowcaseLanding() {
      return (
        this.isLanding &&
        this.whiteLabelBrand.landingVariant === "showcase-cover" &&
        Boolean(this.whiteLabelBrand.landingCover)
      );
    },
    usesInlinePromoLanding() {
      return this.isImmersiveLanding || this.isShowcaseLanding;
    },
    isCoverLanding() {
      return (
        this.isLanding &&
        ["split-cover", "immersive-cover", "showcase-cover"].includes(
          this.whiteLabelBrand.landingVariant
        ) &&
        Boolean(this.whiteLabelBrand.landingCover)
      );
    },
    coverThemeStyle() {
      if (!this.isCoverLanding && !this.isProductLanding) {
        return null;
      }

      const primary =
        this.whiteLabelBrand.dashboardPrimaryColor ||
        this.whiteLabelBrand.authButtonColor ||
        "#272b5c";
      const gradientEnd =
        this.whiteLabelBrand.dashboardPrimaryGradientEnd || primary;
      const accent =
        this.whiteLabelBrand.landingAccentColor ||
        this.whiteLabelBrand.dashboardAccentColor ||
        primary;

      return {
        "--landing-primary": primary,
        "--landing-primary-rgb": this.hexToRgb(primary),
        "--landing-gradient-end": gradientEnd,
        "--landing-accent": accent,
        "--landing-accent-rgb": this.hexToRgb(accent),
      };
    },
    headerClasses() {
      if (this.usesCompactHeader) {
        return ["header-compact"];
      }

      if (this.isProductLanding) {
        return ["ma-4", "page-layout-hero", "cajupe-product-hero"];
      }

      if (this.isCoverLanding) {
        return [
          "ma-4",
          "page-layout-hero",
          "page-layout-hero--cover",
          this.isImmersiveLanding ? "page-layout-hero--immersive" : "",
          this.isDarkImmersiveLanding ? "page-layout-hero--dark-legacy" : "",
          this.isShowcaseLanding ? "page-layout-hero--showcase" : "",
        ];
      }

      return [
        "ma-4",
        "pb-16",
        "pt-16",
        "page-layout-hero",
        this.isLanding ? "bg-gradient-primary" : "",
      ];
    },
    headerContainerClass() {
      if (this.isPreContratacaoPublica) {
        return "py-0";
      }

      if (this.isLanding) {
        return this.isCoverLanding
          ? "page-layout-hero-container page-layout-hero-container--cover"
          : "page-layout-hero-container";
      }

      return "";
    },
    headerRowClass() {
      if (this.usesCompactHeader) {
        return "mt-0";
      }

      if (this.isLanding) {
        return this.isCoverLanding
          ? "page-layout-hero-row page-layout-hero-row--cover"
          : "page-layout-hero-row";
      }

      return "mt-5";
    },
    titleClass() {
      return this.usesCompactHeader ? "mt-0" : "mt-5";
    },
    heroContentColumnClass() {
      if (!this.isLanding) {
        return "";
      }

      return this.isCoverLanding
        ? "page-layout-hero-content page-layout-hero-content--cover"
        : "page-layout-hero-content";
    },
    heroContentClass() {
      if (!this.isLanding) {
        return "";
      }

      return this.isCoverLanding
        ? "page-layout-hero-stack page-layout-hero-stack--cover"
        : "page-layout-hero-stack";
    },
    heroCopyClass() {
      if (!this.isLanding) {
        return "";
      }

      return this.isCoverLanding
        ? "page-layout-hero-copy page-layout-hero-copy--cover"
        : "page-layout-hero-copy";
    },
    landingButtonClass() {
      if (!this.isLanding) {
        return "";
      }

      if (this.isShowcaseLanding) {
        return "page-layout-hero-cta page-layout-hero-cta--cover page-layout-hero-cta--showcase";
      }

      return this.isCoverLanding
        ? "page-layout-hero-cta page-layout-hero-cta--cover white--text"
        : "page-layout-hero-cta";
    },
    landingButtonStyle() {
      if (!this.isCoverLanding && !this.isProductLanding) {
        return null;
      }

      return {
        backgroundColor:
          this.whiteLabelBrand.landingButtonColor ||
          this.whiteLabelBrand.authButtonColor,
      };
    },
    landingButtonLabel() {
      return (
        this.localizedBrand.landingButtonLabel ||
        this.$t("publicPages.login.signIn")
      );
    },
    landingTitleClass() {
      return this.isCoverLanding
        ? "page-layout-hero-title--cover"
        : "text-white";
    },
    landingParagraphClass() {
      return this.isCoverLanding
        ? "page-layout-hero-paragraph--cover"
        : "text-white";
    },
    logoBoxClass() {
      return this.isCoverLanding ? "logo-box--cover" : "";
    },
    titleColorClass() {
      return this.isRegistroPontoWebPublico ? "black--text" : "text-white";
    },
    logoClass() {
      if (this.isCoverLanding) {
        return "hero-image hero-image--cover";
      }

      return this.usesCompactHeader
        ? "hero-image hero-image--compact"
        : "hero-image";
    },
    contentContainerClass() {
      if (this.$route.meta.fullPage) {
        return "pa-0 page-layout-full-page";
      }
      return this.usesCompactHeader ? "pt-0" : "";
    },
    landingLoginRoute() {
      return getLoginRouteByBrand(this.whiteLabelBrand);
    },
    logoSrc() {
      if (
        this.$route.name === "PreContratacaoPublica" &&
        this.publicHeaderImage
      ) {
        return this.publicHeaderImage;
      }
      if (this.isWhiteLabelClient) {
        return this.whiteLabelBrand.publicLogo;
      }
      return require("@/assets/Cajupe_simples.png");
    },
    logoAlt() {
      if (
        this.$route.name === "PreContratacaoPublica" &&
        this.publicHeaderAlt
      ) {
        return this.publicHeaderAlt;
      }
      return this.isWhiteLabelClient ? this.whiteLabelBrand.logoAlt : "Cajupe";
    },
    headerTitleText() {
      switch (this.$route.name) {
        case "Landing":
          return this.isWhiteLabelClient
            ? this.whiteLabelBrand.landingTitle
            : "Cajupe®";
        case "RegistroPontoWebPublico":
          return this.$t("Registro_de_ponto_web");
        default:
          return "";
      }
    },
    headerParagraphText() {
      switch (this.$route.name) {
        case "Landing":
          return this.localizedBrand.landingSubtitle;
        case "RegistroPontoWebPublico":
          return "";
        default:
          return "";
      }
    },
  },
  mounted() {
    window.addEventListener(
      "pre-contratacao-header",
      this.handlePreContratacaoHeader
    );
  },
  beforeDestroy() {
    window.removeEventListener(
      "pre-contratacao-header",
      this.handlePreContratacaoHeader
    );
  },
  watch: {
    $route(to) {
      if (to.name !== "PreContratacaoPublica") {
        this.publicHeaderImage = null;
        this.publicHeaderAlt = null;
      }
    },
  },
  methods: {
    hexToRgb(color) {
      const normalized = String(color || "")
        .replace("#", "")
        .trim();
      const hex =
        normalized.length === 3
          ? normalized
              .split("")
              .map((character) => character + character)
              .join("")
          : normalized;
      const value = Number.parseInt(hex, 16);

      if (hex.length !== 6 || Number.isNaN(value)) {
        return "39, 43, 92";
      }

      return `${(value >> 16) & 255}, ${(value >> 8) & 255}, ${value & 255}`;
    },
    handlePreContratacaoHeader(event) {
      const detail = (event && event.detail) || {};
      this.publicHeaderImage = detail.imagem || null;
      this.publicHeaderAlt = detail.razaoSocial || null;
    },
  },
};
</script>
<style scoped>
.public-language-switcher {
  position: absolute;
  top: 1.25rem;
  right: 1.5rem;
  z-index: 5;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 38px;
  padding: 0.35rem 0.7rem;
  border: 1px solid rgba(52, 71, 103, 0.12);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 6px 20px rgba(31, 35, 48, 0.09);
  backdrop-filter: blur(8px);
}

.public-language-switcher .v-icon {
  color: #596276;
}

.public-language-switcher select {
  max-width: 150px;
  color: #344767;
  background: transparent;
  font-size: 0.75rem;
  font-weight: 700;
  outline: none;
}

.v-enter-active,
.v-leave-active {
  transition: opacity 0.9s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}

.page-layout-main {
  min-height: 100vh;
}

.page-layout-main--cover {
  background: #eef0f7;
}

.page-layout-main--product {
  background: #f3f4f6;
}

.cajupe-product-hero {
  position: relative;
  width: calc(100% - clamp(1.5rem, 3vw, 3rem));
  max-width: 1560px;
  height: calc(100vh - clamp(1.5rem, 3vw, 3rem));
  height: calc(100dvh - clamp(1.5rem, 3vw, 3rem));
  min-height: 640px;
  margin: clamp(0.75rem, 1.5vw, 1.5rem) auto !important;
  padding: 0 !important;
  overflow: hidden;
  border: 1px solid rgba(52, 71, 103, 0.08);
  border-radius: 28px !important;
  background: linear-gradient(145deg, var(--landing-primary) 0%, #8b1813 100%);
  box-shadow: 0 24px 64px rgba(52, 71, 103, 0.14);
}

.cajupe-product-hero::before {
  position: absolute;
  inset: 0;
  z-index: 1;
  width: 44%;
  opacity: 0.18;
  background-image: linear-gradient(
      rgba(255, 255, 255, 0.18) 1px,
      transparent 1px
    ),
    linear-gradient(90deg, rgba(255, 255, 255, 0.18) 1px, transparent 1px);
  background-size: 42px 42px;
  mask-image: linear-gradient(90deg, #000, transparent 92%);
  content: "";
}

.cajupe-product-hero::after {
  position: absolute;
  z-index: 0;
  top: 0;
  right: 0;
  bottom: 0;
  width: 58%;
  border-radius: 46px 0 0 46px;
  background: radial-gradient(
      circle at 88% 10%,
      rgba(var(--landing-primary-rgb), 0.08),
      transparent 27%
    ),
    #f6f7f9;
  content: "";
}

.cajupe-product-hero-container {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: none;
  height: 100%;
  padding: clamp(2rem, 4vw, 4.5rem);
}

.cajupe-product-hero-row {
  height: 100%;
  margin: 0;
  align-items: center;
}

.cajupe-product-hero-content {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: clamp(1.35rem, 3vh, 2rem);
  padding: 0 clamp(1rem, 2vw, 2.5rem) 0 0 !important;
}

.cajupe-product-brand {
  display: inline-flex;
  align-items: center;
  gap: 0.82rem;
  padding: 0.78rem 1.05rem;
  border: 1px solid rgba(255, 255, 255, 0.45);
  border-radius: 14px;
  color: #922019;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 12px 28px rgba(60, 10, 8, 0.18);
}

.cajupe-product-brand img {
  width: 50px;
  height: 70px;
  object-fit: contain;
}

.cajupe-product-brand span {
  font-size: 1.95rem;
  font-weight: 850;
  line-height: 1;
  letter-spacing: -0.045em;
}

.cajupe-product-brand sup {
  top: -0.65em;
  margin-left: 0.12rem;
  font-size: 0.45em;
}

.cajupe-product-copy {
  max-width: 620px;
}

.cajupe-product-eyebrow {
  display: inline-flex;
  align-items: center;
  margin-bottom: 1rem;
  padding: 0.55rem 0.78rem;
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 999px;
  color: rgba(255, 255, 255, 0.9);
  background: rgba(255, 255, 255, 0.08);
  box-shadow: none;
  font-size: 0.74rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.cajupe-product-copy h1 {
  max-width: 650px;
  margin: 0;
  color: #ffffff;
  font-size: clamp(2.65rem, 4vw, 4.3rem);
  font-weight: 850;
  line-height: 0.98;
  letter-spacing: -0.055em;
}

.cajupe-product-copy h1 span {
  display: block;
  color: #ffd6cc;
}

.cajupe-product-copy p {
  max-width: 590px;
  margin: clamp(1.2rem, 2.5vh, 1.8rem) 0 0;
  color: rgba(255, 255, 255, 0.76);
  font-size: clamp(1rem, 1.2vw, 1.15rem);
  line-height: 1.65;
}

.cajupe-product-pill-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
}

.cajupe-product-pill-list span {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.55rem 0.72rem;
  border-radius: 10px;
  color: #535b70;
  background: rgba(255, 255, 255, 0.72);
  box-shadow: inset 0 0 0 1px rgba(var(--landing-primary-rgb), 0.08);
  font-size: 0.8rem;
  font-weight: 700;
}

.cajupe-product-pill-list .v-icon {
  color: var(--landing-primary);
}

.cajupe-product-cta {
  width: min(100%, 350px);
  min-width: min(100%, 350px);
  padding: 0.35rem 0.55rem 0.35rem 1.7rem !important;
  border: 2px solid rgba(255, 255, 255, 0.7) !important;
  border-radius: 16px;
  color: var(--landing-primary) !important;
  background: #ffffff !important;
  box-shadow: 0 18px 38px rgba(60, 10, 8, 0.3),
    0 0 0 6px rgba(255, 255, 255, 0.1) !important;
  font-size: 0.96rem !important;
  font-weight: 850 !important;
  letter-spacing: 0.045em;
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.5);
  transition: transform 180ms ease, box-shadow 180ms ease;
}

.cajupe-product-cta:hover {
  transform: translateY(-3px) scale(1.01);
  box-shadow: 0 24px 45px rgba(60, 10, 8, 0.36),
    0 0 0 7px rgba(255, 255, 255, 0.14) !important;
}

.cajupe-product-cta .v-icon {
  color: var(--landing-primary) !important;
}

.cajupe-product-access {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.65rem;
  width: min(100%, 350px);
}

.cajupe-product-access > span {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  color: rgba(255, 255, 255, 0.82);
  font-size: 0.68rem;
  font-weight: 750;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.cajupe-product-access > span i {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #69d99a;
  box-shadow: 0 0 0 4px rgba(105, 217, 154, 0.14);
}

.cajupe-product-cta-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  margin-left: auto;
  border-radius: 12px;
  background: rgba(var(--landing-primary-rgb), 0.09);
}

.cajupe-product-hero-visual {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 0;
  padding: clamp(1rem, 2vw, 2rem) 0 clamp(1rem, 2vw, 2rem)
    clamp(2rem, 4vw, 5rem) !important;
}

.cajupe-product-orbit {
  position: absolute;
  border: 1px solid rgba(var(--landing-primary-rgb), 0.13);
  border-radius: 50%;
}

.cajupe-product-orbit--one {
  top: 8%;
  right: 1%;
  width: clamp(260px, 30vw, 470px);
  height: clamp(260px, 30vw, 470px);
}

.cajupe-product-orbit--two {
  right: 11%;
  bottom: 3%;
  width: clamp(180px, 19vw, 290px);
  height: clamp(180px, 19vw, 290px);
  border-color: rgba(var(--landing-accent-rgb), 0.2);
}

.cajupe-product-window {
  position: relative;
  z-index: 2;
  width: min(100%, 660px);
  overflow: hidden;
  border: 1px solid rgba(52, 71, 103, 0.1);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.97);
  box-shadow: 0 26px 58px rgba(52, 71, 103, 0.17);
  backdrop-filter: blur(16px);
  transform: none;
}

.cajupe-product-window-header {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  min-height: 58px;
  padding: 0 1.25rem;
  border-bottom: 1px solid #e8eaf0;
  color: #344767;
  background: rgba(255, 255, 255, 0.86);
  font-size: 0.85rem;
}

.cajupe-product-window-dots {
  display: flex;
  gap: 0.35rem;
}

.cajupe-product-window-dots span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #e7d8d5;
}

.cajupe-product-window-dots span:first-child {
  background: var(--landing-primary);
}

.cajupe-product-status {
  justify-self: end;
  padding: 0.4rem 0.62rem;
  border-radius: 999px;
  color: #32805a;
  background: #e9f6ef;
  font-size: 0.68rem;
  font-weight: 800;
}

.cajupe-product-window-body {
  padding: clamp(1.25rem, 2.5vw, 2rem);
  background: #f7f8fa;
}

.cajupe-product-welcome {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 1.25rem;
}

.cajupe-product-welcome small,
.cajupe-product-metrics small,
.cajupe-product-card-heading small,
.cajupe-product-modules > small,
.cajupe-product-mobile-card small {
  display: block;
  color: #9297a7;
  font-size: 0.67rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.cajupe-product-welcome h2 {
  margin: 0.3rem 0 0;
  color: #344767;
  font-size: clamp(1.2rem, 1.8vw, 1.6rem);
  line-height: 1.2;
}

.cajupe-product-welcome > span {
  color: var(--landing-primary);
  font-size: 0.75rem;
  font-weight: 800;
}

.cajupe-product-metrics {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.8rem;
}

.cajupe-product-metrics > div {
  position: relative;
  min-width: 0;
  padding: 1rem;
  border: 1px solid #e9ebf0;
  border-radius: 14px;
  background: #ffffff;
}

.cajupe-product-metric-icon {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 10px;
  color: var(--landing-primary);
  background: rgba(var(--landing-primary-rgb), 0.08);
}

.cajupe-product-metric-icon .v-icon {
  color: var(--landing-primary);
}

.cajupe-product-metrics strong {
  display: block;
  max-width: calc(100% - 30px);
  margin-top: 0.5rem;
  overflow: hidden;
  color: #344767;
  font-size: 0.82rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cajupe-product-flow {
  margin-top: 0.8rem;
  padding: 1rem 1.1rem 0.45rem;
  border: 1px solid #e9ebf0;
  border-radius: 14px;
  background: #ffffff;
}

.cajupe-product-flow-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding-bottom: 0.7rem;
}

.cajupe-product-flow-heading small {
  display: block;
  color: #9297a7;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.cajupe-product-flow-heading strong {
  display: block;
  margin-top: 0.22rem;
  color: #344767;
  font-size: 0.8rem;
}

.cajupe-product-flow-heading > span {
  color: #408563;
  font-size: 0.67rem;
  font-weight: 800;
  white-space: nowrap;
}

.cajupe-product-flow-row {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.75rem;
  padding: 0.65rem 0;
  border-top: 1px solid #eef0f3;
}

.cajupe-product-flow-row > span {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 9px;
  background: rgba(var(--landing-primary-rgb), 0.08);
}

.cajupe-product-flow-row .v-icon {
  color: var(--landing-primary);
}

.cajupe-product-flow-row p {
  min-width: 0;
  margin: 0;
  overflow: hidden;
  color: #9a9eaa;
  font-size: 0.66rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cajupe-product-flow-row p strong {
  display: block;
  overflow: hidden;
  color: #495269;
  font-size: 0.75rem;
  text-overflow: ellipsis;
}

.cajupe-product-flow-row em {
  color: #a5a9b5;
  font-size: 0.62rem;
  font-style: normal;
  white-space: nowrap;
}

.cajupe-product-dashboard-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.25fr) minmax(190px, 0.75fr);
  gap: 0.8rem;
  margin-top: 0.8rem;
}

.cajupe-product-activity,
.cajupe-product-modules {
  min-width: 0;
  padding: 1rem;
  border: 1px solid #eee8e7;
  border-radius: 14px;
  background: #ffffff;
}

.cajupe-product-card-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.cajupe-product-card-heading strong {
  display: block;
  margin-top: 0.25rem;
  color: #3a4055;
  font-size: 0.82rem;
}

.cajupe-product-card-heading > span {
  color: #32805a;
  font-size: 0.72rem;
  font-weight: 800;
}

.cajupe-product-chart {
  display: flex;
  align-items: flex-end;
  gap: clamp(0.35rem, 1vw, 0.75rem);
  height: 116px;
  margin-top: 1rem;
  padding-top: 0.5rem;
  border-bottom: 1px solid #e8e3e2;
}

.cajupe-product-chart span {
  flex: 1;
  min-width: 8px;
  border-radius: 6px 6px 2px 2px;
  background: linear-gradient(
    180deg,
    var(--landing-accent),
    var(--landing-primary)
  );
  opacity: 0.88;
}

.cajupe-product-modules > div {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 0.65rem;
  padding: 0.72rem 0;
  border-bottom: 1px solid #f0eceb;
}

.cajupe-product-modules > div:last-child {
  border-bottom: 0;
}

.cajupe-product-modules > div > span {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 9px;
  background: rgba(var(--landing-primary-rgb), 0.08);
}

.cajupe-product-modules .v-icon {
  color: var(--landing-primary);
}

.cajupe-product-modules p {
  min-width: 0;
  margin: 0;
  color: #999eac;
  font-size: 0.67rem;
}

.cajupe-product-modules p strong {
  display: block;
  overflow: hidden;
  color: #464c60;
  font-size: 0.75rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cajupe-product-modules i,
.cajupe-product-mobile-card i {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #49aa7b;
  box-shadow: 0 0 0 4px #e9f6ef;
}

.cajupe-product-mobile-card {
  position: absolute;
  z-index: 3;
  right: -1%;
  bottom: 13%;
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 0.75rem;
  min-width: 250px;
  padding: 0.9rem 1rem;
  border: 1px solid rgba(var(--landing-primary-rgb), 0.1);
  border-radius: 15px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 18px 45px rgba(50, 31, 37, 0.2);
}

.cajupe-product-mobile-card > span {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 11px;
  background: rgba(var(--landing-primary-rgb), 0.09);
}

.cajupe-product-mobile-card .v-icon {
  color: var(--landing-primary);
}

.cajupe-product-mobile-card strong {
  display: block;
  margin-top: 0.16rem;
  color: #3e4459;
  font-size: 0.78rem;
}

.page-layout-full-page {
  max-width: none;
}

.page-layout-hero {
  display: flex;
  align-items: center;
  min-height: calc(100vh - 2rem);
  min-height: calc(100dvh - 2rem);
}

.page-layout-hero--cover {
  width: calc(100% - clamp(1.5rem, 3vw, 3rem));
  max-width: 1560px;
  height: calc(100vh - clamp(1.5rem, 3vw, 3rem));
  height: calc(100dvh - clamp(1.5rem, 3vw, 3rem));
  min-height: 0;
  margin: clamp(0.75rem, 1.5vw, 1.5rem) auto !important;
  padding: 0 !important;
  overflow: hidden;
  border: 1px solid rgba(var(--landing-primary-rgb), 0.08);
  border-radius: 28px !important;
  background: #ffffff;
  box-shadow: 0 28px 70px rgba(var(--landing-primary-rgb), 0.16);
}

.page-layout-hero-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: stretch;
}

.page-layout-hero-container--cover {
  max-width: none;
  padding: 0;
}

.page-layout-hero-row {
  flex: 1;
  width: 100%;
  min-height: 100%;
  align-items: center;
}

.page-layout-hero-row--cover {
  position: relative;
  height: 100%;
  min-height: 0;
  margin: 0;
}

.page-layout-hero-content {
  display: flex;
}

.page-layout-hero-content--cover {
  order: 2;
  position: relative;
  z-index: 3;
  height: 100%;
  min-height: 0;
  padding: clamp(2rem, 4vw, 4.5rem) !important;
  background: radial-gradient(
      circle at 100% 0,
      rgba(var(--landing-accent-rgb), 0.12),
      transparent 28%
    ),
    linear-gradient(155deg, #ffffff 0%, #f7f8fc 100%);
}

.page-layout-hero-content--cover::before {
  position: absolute;
  right: -80px;
  bottom: -105px;
  width: 230px;
  height: 230px;
  border: 42px solid rgba(var(--landing-primary-rgb), 0.035);
  border-radius: 50%;
  content: "";
}

.page-layout-hero-stack {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  align-items: center;
  gap: clamp(1.5rem, 4vh, 3rem);
  width: 100%;
}

.page-layout-hero-stack--cover {
  align-items: flex-start;
  justify-content: center;
  gap: clamp(1.25rem, 3.2vh, 2.25rem);
  max-width: 460px;
  margin: 0 auto;
}

.page-layout-hero-copy {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: clamp(0.75rem, 1.5vh, 1.25rem);
  width: 100%;
}

.page-layout-hero-copy--cover {
  align-items: flex-start;
  gap: clamp(0.65rem, 1.4vh, 1rem);
}

.page-layout-hero-copy-accent {
  display: block;
  width: 58px;
  height: 4px;
  margin-bottom: clamp(0.3rem, 1vh, 0.75rem);
  border-radius: 999px;
  background: var(--landing-accent);
}

.page-layout-hero-title--cover {
  color: var(--landing-primary);
  font-size: clamp(2rem, 3vw, 3.25rem) !important;
  line-height: 1.08;
  letter-spacing: -0.035em !important;
  text-align: left !important;
}

.page-layout-hero-paragraph--cover {
  color: #74778a;
  font-size: clamp(1rem, 1.25vw, 1.15rem) !important;
  text-align: left !important;
  line-height: 1.6;
}

.page-layout-hero-copy > * {
  margin-bottom: 0 !important;
}

.page-layout-hero-cta {
  max-width: min(420px, 100%);
  margin: 0 auto !important;
}

.page-layout-hero-cta--cover {
  max-width: 340px;
  height: 50px !important;
  margin-left: 0 !important;
  border-radius: 12px;
  box-shadow: 0 14px 30px rgba(var(--landing-primary-rgb), 0.24) !important;
  letter-spacing: 0.055em;
  transition: transform 180ms ease, box-shadow 180ms ease;
}

.page-layout-hero-cta--cover:hover {
  transform: translateY(-2px);
  box-shadow: 0 18px 34px rgba(var(--landing-primary-rgb), 0.3) !important;
}

.logo-box {
  grid-column: 0;
  align-self: center;
  justify-self: center;

  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 3em;
}

.logo-box--cover {
  align-items: flex-start;
  width: 100%;
  padding: 0;
}

.hero-image {
  display: block;
  max-width: 60vw;
  max-height: 45vh;
  object-fit: contain;
}

.hero-image--cover {
  width: min(100%, 350px);
  max-width: 100%;
  max-height: none;
}

.page-layout-hero-media {
  order: 1;
  position: relative;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  background: var(--landing-primary);
}

.page-layout-hero-media::after {
  position: absolute;
  z-index: 1;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(var(--landing-primary-rgb), 0.02) 28%,
    rgba(var(--landing-primary-rgb), 0.9) 100%
  );
  content: "";
}

.page-layout-hero-cover {
  display: block;
  width: 100%;
  height: 100%;
  min-height: 0;
  object-fit: cover;
  object-position: center top;
}

.page-layout-hero-media-copy {
  position: absolute;
  z-index: 2;
  right: clamp(2rem, 5vw, 5rem);
  bottom: clamp(2rem, 6vh, 4.5rem);
  left: clamp(2rem, 5vw, 5rem);
  max-width: 680px;
  color: #ffffff;
}

.page-layout-hero-media-copy span {
  display: inline-flex;
  align-items: center;
  width: fit-content;
  margin-bottom: 0.75rem;
  padding: 0.55rem 0.8rem;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 999px;
  color: var(--landing-accent);
  background: rgba(var(--landing-primary-rgb), 0.9);
  box-shadow: 0 8px 24px rgba(20, 23, 54, 0.18);
  backdrop-filter: blur(8px);
  font-size: 0.78rem;
  font-weight: 800;
  line-height: 1.2;
  letter-spacing: 0.11em;
  text-transform: uppercase;
}

.page-layout-hero-media-copy h3 {
  max-width: 620px;
  margin: 0 0 0.85rem;
  color: #ffffff;
  font-size: clamp(2rem, 3.2vw, 3.5rem);
  font-weight: 800;
  line-height: 1.08;
  letter-spacing: -0.035em;
}

.page-layout-hero-media-copy p {
  max-width: 630px;
  margin: 0;
  color: rgba(255, 255, 255, 0.82);
  font-size: clamp(0.95rem, 1.2vw, 1.12rem);
  line-height: 1.6;
}

.page-layout-hero--immersive {
  border-color: rgba(var(--landing-primary-rgb), 0.14);
  background: var(--landing-primary);
}

.page-layout-hero--immersive .page-layout-hero-media--immersive {
  position: absolute;
  z-index: 0;
  inset: 0;
  order: initial;
  width: 100%;
  max-width: 100%;
  height: 100%;
  flex: 0 0 100%;
}

.page-layout-hero--immersive .page-layout-hero-media--immersive::after {
  background: linear-gradient(
      90deg,
      rgba(var(--landing-primary-rgb), 0.5) 0%,
      rgba(var(--landing-primary-rgb), 0.18) 42%,
      rgba(var(--landing-primary-rgb), 0.02) 72%
    ),
    linear-gradient(
      0deg,
      rgba(var(--landing-primary-rgb), 0.26) 0%,
      transparent 46%
    );
}

.page-layout-hero--immersive .page-layout-hero-cover {
  object-position: center center;
}

.page-layout-hero--immersive .page-layout-hero-content--cover {
  position: absolute;
  z-index: 3;
  top: 50%;
  left: clamp(1.5rem, 4vw, 4rem);
  width: min(520px, calc(100% - clamp(3rem, 8vw, 8rem)));
  max-width: 520px;
  height: auto;
  max-height: calc(100% - clamp(3rem, 8vh, 6rem));
  padding: clamp(2rem, 3vw, 3.25rem) !important;
  overflow-y: auto;
  border: 1px solid rgba(255, 255, 255, 0.72);
  border-radius: 26px;
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 28px 70px rgba(var(--landing-primary-rgb), 0.3);
  backdrop-filter: blur(14px);
  transform: translateY(-50%);
  flex: 0 0 auto;
}

.page-layout-hero--immersive .page-layout-hero-content--cover::before {
  display: none;
}

.page-layout-hero--immersive .page-layout-hero-stack--cover {
  align-items: flex-start;
  gap: clamp(0.9rem, 2vh, 1.35rem);
  max-width: none;
  margin: 0;
}

.page-layout-hero--immersive .hero-image--cover {
  width: min(100%, 280px);
}

.page-layout-hero-immersive-promo {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.7rem;
}

.page-layout-hero-immersive-promo span {
  color: var(--landing-primary);
  font-size: 0.75rem;
  font-weight: 800;
  line-height: 1.2;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.page-layout-hero-immersive-promo h3 {
  margin: 0;
  color: var(--landing-primary);
  font-size: clamp(1.8rem, 2.5vw, 2.65rem);
  font-weight: 800;
  line-height: 1.08;
  letter-spacing: -0.035em;
}

.page-layout-hero-immersive-promo p {
  margin: 0;
  color: #606477;
  font-size: clamp(0.94rem, 1.05vw, 1.05rem);
  line-height: 1.55;
}

.page-layout-hero--immersive .page-layout-hero-copy--cover {
  padding-top: clamp(0.3rem, 1vh, 0.65rem);
  border-top: 1px solid rgba(var(--landing-primary-rgb), 0.12);
}

.page-layout-hero--immersive .page-layout-hero-copy-accent {
  display: none;
}

.page-layout-hero--immersive .page-layout-hero-paragraph--cover {
  color: #74778a;
  font-size: 0.92rem !important;
}

.page-layout-hero--dark-legacy .page-layout-hero-media--immersive::after {
  background: linear-gradient(
      270deg,
      rgba(var(--landing-primary-rgb), 0.42) 0%,
      rgba(var(--landing-primary-rgb), 0.12) 42%,
      rgba(var(--landing-primary-rgb), 0.02) 74%
    ),
    linear-gradient(
      0deg,
      rgba(var(--landing-primary-rgb), 0.32) 0%,
      transparent 50%
    );
}

.page-layout-hero--dark-legacy .page-layout-hero-cover {
  object-position: 33% center;
}

.page-layout-hero--dark-legacy .page-layout-hero-content--cover {
  right: clamp(1.5rem, 4vw, 4rem);
  left: auto;
  width: min(500px, calc(100% - clamp(3rem, 8vw, 8rem)));
  max-width: 500px;
  border-color: rgba(var(--landing-accent-rgb), 0.68);
  background: radial-gradient(
      circle at 50% 0,
      rgba(var(--landing-accent-rgb), 0.16),
      transparent 34%
    ),
    linear-gradient(145deg, rgba(255, 255, 255, 0.98), #f7f3e8);
  box-shadow: 0 30px 80px rgba(8, 12, 40, 0.34);
}

.page-layout-hero--dark-legacy .page-layout-hero-stack--cover,
.page-layout-hero--dark-legacy .logo-box--cover,
.page-layout-hero--dark-legacy .page-layout-hero-copy--cover,
.page-layout-hero--dark-legacy .page-layout-hero-immersive-promo {
  align-items: center;
}

.page-layout-hero--dark-legacy .hero-image--cover {
  width: min(100%, 185px);
  filter: drop-shadow(0 16px 30px rgba(0, 0, 0, 0.35));
}

.page-layout-hero--dark-legacy .page-layout-hero-immersive-promo span {
  padding: 0.48rem 0.72rem;
  border: 1px solid rgba(var(--landing-accent-rgb), 0.36);
  border-radius: 999px;
  color: var(--landing-accent);
  background: rgba(var(--landing-accent-rgb), 0.08);
  text-align: center;
}

.page-layout-hero--dark-legacy .page-layout-hero-immersive-promo h3 {
  color: var(--landing-primary);
  font-size: clamp(1.8rem, 2.35vw, 2.5rem);
  text-align: center;
}

.page-layout-hero--dark-legacy .page-layout-hero-immersive-promo p {
  color: #626678;
  text-align: center;
}

.page-layout-hero--dark-legacy .page-layout-hero-copy--cover {
  border-top-color: rgba(var(--landing-primary-rgb), 0.14);
}

.page-layout-hero--dark-legacy .page-layout-hero-paragraph--cover {
  color: #74778a;
  text-align: center !important;
}

.page-layout-hero--dark-legacy .page-layout-hero-cta--cover {
  margin-right: auto !important;
  margin-left: auto !important;
  color: var(--landing-primary) !important;
  box-shadow: 0 16px 34px rgba(8, 12, 40, 0.42) !important;
}

.page-layout-hero--showcase {
  border-color: rgba(var(--landing-accent-rgb), 0.38);
  background: var(--landing-primary);
  box-shadow: 0 30px 80px rgba(var(--landing-primary-rgb), 0.3);
}

.page-layout-hero--showcase .page-layout-hero-media--showcase {
  position: absolute;
  z-index: 0;
  top: 0;
  right: 0;
  left: 0;
  order: initial;
  width: 100%;
  max-width: 100%;
  height: 68%;
  flex: 0 0 100%;
}

.page-layout-hero--showcase .page-layout-hero-media--showcase::after {
  background: linear-gradient(
      180deg,
      rgba(var(--landing-primary-rgb), 0.02) 40%,
      rgba(var(--landing-primary-rgb), 0.3) 72%,
      var(--landing-primary) 100%
    ),
    linear-gradient(
      90deg,
      rgba(var(--landing-primary-rgb), 0.22),
      transparent 36%,
      rgba(var(--landing-primary-rgb), 0.12)
    );
}

.page-layout-hero--showcase .page-layout-hero-cover {
  object-position: center 34%;
}

.page-layout-hero--showcase .page-layout-hero-content--cover {
  position: absolute;
  z-index: 3;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  max-width: 100%;
  min-height: 38%;
  height: auto;
  padding: clamp(1.75rem, 3.5vw, 3.25rem) clamp(2rem, 5vw, 5rem) !important;
  overflow: hidden;
  border-top: 2px solid var(--landing-accent);
  background: radial-gradient(
      circle at 92% 130%,
      rgba(var(--landing-accent-rgb), 0.16),
      transparent 32%
    ),
    linear-gradient(
      112deg,
      var(--landing-primary) 0%,
      var(--landing-gradient-end) 100%
    );
  flex: 0 0 100%;
}

.page-layout-hero--showcase .page-layout-hero-content--cover::before {
  z-index: 0;
  right: clamp(1rem, 4vw, 4rem);
  bottom: -4.5rem;
  width: auto;
  height: auto;
  border: 0;
  border-radius: 0;
  color: rgba(var(--landing-accent-rgb), 0.07);
  font-size: clamp(10rem, 18vw, 18rem);
  font-weight: 900;
  line-height: 1;
  content: "50";
}

.page-layout-hero--showcase .page-layout-hero-stack--cover {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-areas:
    "logo promo access"
    "logo system access";
  grid-template-columns:
    minmax(110px, 150px) minmax(320px, 1fr)
    minmax(230px, 300px);
  grid-template-rows: auto auto;
  align-items: center;
  gap: 0.65rem clamp(1.75rem, 4vw, 4rem);
  max-width: 1320px;
  margin: 0 auto;
}

.page-layout-hero--showcase .logo-box--cover {
  grid-area: logo;
  align-items: center;
  justify-content: center;
  width: auto;
}

.page-layout-hero--showcase .hero-image--cover {
  width: min(100%, 142px);
  filter: drop-shadow(0 12px 24px rgba(8, 12, 40, 0.28));
}

.page-layout-hero--showcase .page-layout-hero-immersive-promo {
  grid-area: promo;
  gap: 0.45rem;
}

.page-layout-hero--showcase .page-layout-hero-immersive-promo span {
  color: var(--landing-accent);
}

.page-layout-hero--showcase .page-layout-hero-immersive-promo h3 {
  max-width: 700px;
  color: #ffffff;
  font-size: clamp(1.65rem, 2.4vw, 2.6rem);
}

.page-layout-hero--showcase .page-layout-hero-immersive-promo p {
  max-width: 720px;
  color: rgba(255, 255, 255, 0.74);
}

.page-layout-hero--showcase .page-layout-hero-copy--cover {
  grid-area: system;
  align-items: flex-start;
}

.page-layout-hero--showcase .page-layout-hero-copy-accent {
  display: none;
}

.page-layout-hero--showcase .page-layout-hero-paragraph--cover {
  color: rgba(255, 255, 255, 0.72);
  font-size: 0.9rem !important;
  letter-spacing: 0.02em;
}

.page-layout-hero--showcase .page-layout-hero-cta--showcase {
  grid-area: access;
  width: 100%;
  max-width: 300px;
  height: 54px !important;
  margin: 0 !important;
  color: var(--landing-primary) !important;
  box-shadow: 0 16px 34px rgba(8, 12, 40, 0.32) !important;
}

.hero-image--compact {
  max-height: 120px;
}

/* Páginas públicas (pré-contratação, ponto web): logo menor no celular para o formulário aparecer antes. */
@media (max-width: 599px) {
  .hero-image--compact {
    max-height: 72px;
  }
}

.header-compact {
  margin: 0;
  padding: 0.5rem 0;
}

@media (max-width: 1199px) {
  .cajupe-product-hero-container {
    padding: clamp(2rem, 3.5vw, 3rem);
  }

  .cajupe-product-copy h1 {
    font-size: clamp(2.65rem, 4.2vw, 3.8rem);
  }

  .cajupe-product-window-body {
    padding: 1.35rem;
  }

  .cajupe-product-mobile-card {
    right: -0.5rem;
    bottom: 8%;
  }
}

@media (max-width: 959px) {
  .page-layout-hero {
    min-height: auto;
  }

  .page-layout-hero-row {
    min-height: auto;
  }

  .page-layout-hero-stack {
    justify-content: center;
  }

  .page-layout-hero--cover {
    height: auto;
    min-height: calc(100vh - 2rem);
    min-height: calc(100dvh - 2rem);
    border-radius: 22px !important;
  }

  .page-layout-hero-container--cover,
  .page-layout-hero-row--cover {
    height: auto;
    min-height: 0;
  }

  .page-layout-hero-content--cover {
    order: 2;
    position: relative;
    height: auto;
    min-height: 0;
    padding: clamp(2rem, 6vw, 3.5rem) !important;
  }

  .page-layout-hero-stack--cover {
    align-items: center;
    gap: clamp(1rem, 2.5vh, 1.5rem);
    max-width: 560px;
  }

  .logo-box--cover,
  .page-layout-hero-copy--cover {
    align-items: center;
  }

  .page-layout-hero-title--cover,
  .page-layout-hero-paragraph--cover {
    text-align: center !important;
  }

  .page-layout-hero-cta--cover {
    max-width: 100%;
    margin-left: auto !important;
  }

  .hero-image--cover {
    width: min(100%, 300px);
  }

  .page-layout-hero-media {
    order: 1;
    position: relative;
    width: 100%;
    max-width: none;
    height: clamp(280px, 40vh, 430px);
    min-height: 280px;
  }

  .page-layout-hero-cover {
    height: 100%;
    min-height: 0;
    object-position: center top;
  }

  .page-layout-hero-title--cover {
    font-size: clamp(1.75rem, 5vw, 2.5rem) !important;
  }

  .page-layout-hero-paragraph--cover {
    font-size: clamp(0.95rem, 3.8vw, 1.1rem) !important;
  }

  .page-layout-hero-media-copy {
    right: clamp(1.5rem, 5vw, 3rem);
    bottom: clamp(1.5rem, 4vh, 2.5rem);
    left: clamp(1.5rem, 5vw, 3rem);
  }

  .page-layout-hero-media-copy h3 {
    font-size: clamp(1.75rem, 5.5vw, 2.6rem);
  }

  .page-layout-hero--immersive {
    height: calc(100vh - 2rem);
    height: calc(100dvh - 2rem);
    min-height: 540px;
  }

  .page-layout-hero--immersive .page-layout-hero-container--cover,
  .page-layout-hero--immersive .page-layout-hero-row--cover {
    height: 100%;
  }

  .page-layout-hero--immersive .page-layout-hero-media--immersive {
    position: absolute;
    inset: 0;
    height: 100%;
    min-height: 0;
  }

  .page-layout-hero--immersive .page-layout-hero-cover {
    object-position: 64% center;
  }

  .page-layout-hero--immersive .page-layout-hero-content--cover {
    position: absolute;
    top: auto;
    bottom: clamp(1rem, 3vw, 1.5rem);
    left: clamp(1rem, 3vw, 1.5rem);
    width: min(560px, calc(100% - clamp(2rem, 6vw, 3rem)));
    max-width: 560px;
    max-height: calc(100% - clamp(2rem, 6vw, 3rem));
    padding: clamp(1.5rem, 4vw, 2.25rem) !important;
    transform: none;
  }

  .page-layout-hero--immersive .page-layout-hero-stack--cover,
  .page-layout-hero--immersive .logo-box--cover,
  .page-layout-hero--immersive .page-layout-hero-copy--cover {
    align-items: flex-start;
  }

  .page-layout-hero--immersive .page-layout-hero-paragraph--cover {
    text-align: left !important;
  }

  .page-layout-hero--immersive .page-layout-hero-cta--cover {
    margin-left: 0 !important;
  }

  .page-layout-hero--showcase {
    height: calc(100vh - 2rem);
    height: calc(100dvh - 2rem);
    min-height: 600px;
  }

  .page-layout-hero--showcase .page-layout-hero-container--cover,
  .page-layout-hero--showcase .page-layout-hero-row--cover {
    height: 100%;
  }

  .page-layout-hero--showcase .page-layout-hero-media--showcase {
    position: absolute;
    top: 0;
    height: 44%;
    min-height: 0;
  }

  .page-layout-hero--showcase .page-layout-hero-cover {
    object-position: center center;
  }

  .page-layout-hero--showcase .page-layout-hero-content--cover {
    position: absolute;
    top: 40%;
    right: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    max-width: 100%;
    height: auto;
    min-height: 0;
    padding: clamp(1.75rem, 5vw, 2.5rem) !important;
  }

  .page-layout-hero--showcase .page-layout-hero-stack--cover {
    grid-template-areas:
      "logo promo"
      "logo system"
      "access access";
    grid-template-columns: minmax(90px, 120px) minmax(0, 1fr);
    gap: 0.6rem clamp(1.25rem, 4vw, 2.5rem);
    max-width: 720px;
  }

  .page-layout-hero--showcase .logo-box--cover,
  .page-layout-hero--showcase .page-layout-hero-copy--cover {
    align-items: flex-start;
  }

  .page-layout-hero--showcase .hero-image--cover {
    width: min(100%, 115px);
  }

  .page-layout-hero--showcase .page-layout-hero-paragraph--cover {
    text-align: left !important;
  }

  .page-layout-hero--showcase .page-layout-hero-cta--showcase {
    max-width: 100%;
    margin-top: 0.5rem !important;
  }

  .page-layout-hero--dark-legacy .page-layout-hero-cover {
    object-position: 32% center;
  }

  .page-layout-hero--dark-legacy .page-layout-hero-content--cover {
    right: clamp(1rem, 3vw, 1.5rem);
    left: auto;
    width: min(500px, calc(100% - clamp(2rem, 6vw, 3rem)));
    max-width: 500px;
    transform: none;
  }

  .page-layout-hero--dark-legacy .page-layout-hero-stack--cover,
  .page-layout-hero--dark-legacy .logo-box--cover,
  .page-layout-hero--dark-legacy .page-layout-hero-copy--cover {
    align-items: center;
  }

  .page-layout-hero--dark-legacy .page-layout-hero-cta--cover {
    margin-right: auto !important;
    margin-left: auto !important;
  }

  .cajupe-product-hero {
    width: calc(100% - 2rem);
    height: auto;
    min-height: calc(100vh - 2rem);
    min-height: calc(100dvh - 2rem);
    border-radius: 22px !important;
  }

  .cajupe-product-hero::before {
    width: 100%;
    height: 48%;
    mask-image: linear-gradient(180deg, #000, transparent 95%);
  }

  .cajupe-product-hero::after {
    top: 48%;
    width: 100%;
    border-radius: 38px 38px 0 0;
  }

  .cajupe-product-hero-container,
  .cajupe-product-hero-row {
    height: auto;
    min-height: 0;
  }

  .cajupe-product-hero-container {
    padding: clamp(2rem, 6vw, 3.5rem);
  }

  .cajupe-product-hero-content {
    align-items: center;
    gap: 1.2rem;
    max-width: 730px;
    margin: 0 auto;
    padding: 0 !important;
    text-align: center;
  }

  .cajupe-product-copy p {
    margin-right: auto;
    margin-left: auto;
  }

  .cajupe-product-access {
    align-items: center;
  }

  .cajupe-product-pill-list {
    justify-content: center;
  }

  .cajupe-product-hero-visual {
    height: clamp(410px, 52vh, 500px);
    margin-top: 1.25rem;
    padding: 1.5rem 0 0 !important;
  }

  .cajupe-product-window {
    width: min(100%, 660px);
    transform: none;
  }

  .cajupe-product-mobile-card {
    right: 0.5rem;
    bottom: 0;
  }
}

@media (max-width: 599px) {
  .public-language-switcher {
    top: 0.85rem;
    right: 0.85rem;
  }

  .page-layout-hero--cover {
    width: calc(100% - 1.5rem);
    min-height: calc(100vh - 1.5rem);
    min-height: calc(100dvh - 1.5rem);
    margin: 0.75rem auto !important;
    border-radius: 18px !important;
  }

  .page-layout-hero-media {
    height: 42vh;
    min-height: 280px;
    max-height: 390px;
  }

  .page-layout-hero-media-copy span {
    margin-bottom: 0.5rem;
    padding: 0.45rem 0.65rem;
    font-size: 0.68rem;
  }

  .page-layout-hero-media-copy h3 {
    margin-bottom: 0;
    font-size: clamp(1.5rem, 7vw, 2rem);
  }

  .page-layout-hero-media-copy p {
    display: none;
  }

  .page-layout-hero-content--cover {
    padding: 2rem 1.5rem !important;
  }

  .page-layout-hero-stack--cover {
    gap: 1rem;
  }

  .hero-image--cover {
    width: min(100%, 250px);
  }

  .page-layout-hero-copy-accent {
    margin-right: auto;
    margin-left: auto;
  }

  .page-layout-hero-cta--cover {
    height: 48px !important;
  }

  .page-layout-hero--immersive {
    width: calc(100% - 1rem);
    height: calc(100vh - 1rem);
    height: calc(100dvh - 1rem);
    min-height: 510px;
    margin: 0.5rem auto !important;
  }

  .page-layout-hero--immersive .page-layout-hero-cover {
    object-position: 68% center;
  }

  .page-layout-hero--immersive .page-layout-hero-content--cover {
    bottom: 0.65rem;
    left: 0.65rem;
    width: calc(100% - 1.3rem);
    max-height: calc(100% - 1.3rem);
    padding: 1.35rem !important;
    border-radius: 18px;
  }

  .page-layout-hero--immersive .page-layout-hero-stack--cover {
    gap: 0.75rem;
  }

  .page-layout-hero--immersive .hero-image--cover {
    width: min(100%, 220px);
  }

  .page-layout-hero-immersive-promo {
    gap: 0.45rem;
  }

  .page-layout-hero-immersive-promo h3 {
    font-size: clamp(1.45rem, 6.4vw, 1.9rem);
  }

  .page-layout-hero-immersive-promo p {
    display: none;
  }

  .page-layout-hero--showcase {
    width: calc(100% - 1rem);
    height: calc(100vh - 1rem);
    height: calc(100dvh - 1rem);
    min-height: 570px;
    margin: 0.5rem auto !important;
  }

  .page-layout-hero--showcase .page-layout-hero-media--showcase {
    height: 35%;
  }

  .page-layout-hero--showcase .page-layout-hero-cover {
    object-position: 48% center;
  }

  .page-layout-hero--showcase .page-layout-hero-content--cover {
    top: 31%;
    padding: 1.25rem !important;
  }

  .page-layout-hero--showcase .page-layout-hero-stack--cover {
    display: flex;
    align-items: flex-start;
    justify-content: center;
    gap: 0.65rem;
  }

  .page-layout-hero--showcase .hero-image--cover {
    width: 84px;
  }

  .page-layout-hero--showcase .page-layout-hero-immersive-promo h3 {
    font-size: clamp(1.35rem, 6.2vw, 1.75rem);
  }

  .page-layout-hero--showcase .page-layout-hero-copy--cover {
    width: 100%;
  }

  .page-layout-hero--dark-legacy .page-layout-hero-content--cover {
    right: 0.65rem;
    left: auto;
    width: calc(100% - 1.3rem);
    transform: none;
  }

  .page-layout-hero--dark-legacy .hero-image--cover {
    width: 112px;
  }

  .page-layout-hero--dark-legacy .page-layout-hero-immersive-promo span {
    padding: 0.4rem 0.6rem;
    font-size: 0.64rem;
  }

  .page-layout-hero--dark-legacy .page-layout-hero-immersive-promo h3 {
    font-size: clamp(1.4rem, 6.3vw, 1.8rem);
  }

  .cajupe-product-hero {
    width: calc(100% - 1rem);
    min-height: calc(100vh - 1rem);
    min-height: calc(100dvh - 1rem);
    margin: 0.5rem auto !important;
    border-radius: 18px !important;
  }

  .cajupe-product-hero::before {
    height: 49%;
    mask-image: linear-gradient(180deg, #000, transparent 90%);
  }

  .cajupe-product-hero::after {
    top: 49%;
    border-radius: 26px 26px 0 0;
  }

  .cajupe-product-hero-container {
    padding: 1.65rem 1rem 1.25rem;
  }

  .cajupe-product-hero-content {
    gap: 0.9rem;
  }

  .cajupe-product-brand {
    gap: 0.62rem;
    padding: 0.64rem 0.82rem;
  }

  .cajupe-product-brand img {
    width: 35px;
    height: 49px;
  }

  .cajupe-product-brand span {
    font-size: 1.45rem;
  }

  .cajupe-product-eyebrow {
    margin-bottom: 0.7rem;
    padding: 0.43rem 0.62rem;
    font-size: 0.63rem;
  }

  .cajupe-product-copy h1 {
    font-size: clamp(2.05rem, 10vw, 2.75rem);
    line-height: 1;
  }

  .cajupe-product-copy p {
    margin-top: 0.85rem;
    font-size: 0.87rem;
    line-height: 1.48;
  }

  .cajupe-product-pill-list {
    gap: 0.42rem;
  }

  .cajupe-product-pill-list span {
    gap: 0.35rem;
    padding: 0.45rem 0.55rem;
    font-size: 0.69rem;
  }

  .cajupe-product-pill-list .v-icon {
    font-size: 12px !important;
  }

  .cajupe-product-cta {
    width: 100%;
    min-width: 0;
    height: 56px !important;
    padding-left: 1.25rem !important;
  }

  .cajupe-product-access {
    width: 100%;
  }

  .cajupe-product-cta-icon {
    width: 36px;
    height: 36px;
  }

  .cajupe-product-hero-visual {
    height: 345px;
    margin-top: 0.5rem;
    padding-top: 1rem !important;
  }

  .cajupe-product-orbit--one {
    top: 2%;
    right: -15%;
    width: 250px;
    height: 250px;
  }

  .cajupe-product-orbit--two {
    right: auto;
    bottom: 0;
    left: -12%;
    width: 170px;
    height: 170px;
  }

  .cajupe-product-window {
    border-radius: 17px;
  }

  .cajupe-product-window-header {
    grid-template-columns: 1fr auto 1fr;
    min-height: 46px;
    padding: 0 0.8rem;
    font-size: 0.72rem;
  }

  .cajupe-product-status {
    display: none;
  }

  .cajupe-product-window-body {
    padding: 0.85rem;
  }

  .cajupe-product-welcome {
    margin-bottom: 0.7rem;
  }

  .cajupe-product-welcome h2 {
    font-size: 1rem;
  }

  .cajupe-product-welcome > span {
    font-size: 0.67rem;
  }

  .cajupe-product-metrics {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.55rem;
  }

  .cajupe-product-metrics > div {
    padding: 0.7rem;
  }

  .cajupe-product-metrics > div:nth-child(3) {
    display: none;
  }

  .cajupe-product-metric-icon {
    top: 0.6rem;
    right: 0.6rem;
    width: 26px;
    height: 26px;
  }

  .cajupe-product-metrics strong {
    margin-top: 0.35rem;
    font-size: 0.7rem;
  }

  .cajupe-product-dashboard-grid {
    grid-template-columns: 1fr;
    gap: 0;
    margin-top: 0.55rem;
  }

  .cajupe-product-activity {
    padding: 0.75rem;
  }

  .cajupe-product-modules,
  .cajupe-product-mobile-card {
    display: none;
  }

  .cajupe-product-flow {
    margin-top: 0.55rem;
    padding: 0.65rem 0.75rem 0.2rem;
  }

  .cajupe-product-flow-heading {
    padding-bottom: 0.45rem;
  }

  .cajupe-product-flow-heading strong {
    font-size: 0.7rem;
  }

  .cajupe-product-flow-heading > span,
  .cajupe-product-flow-row:nth-child(4) {
    display: none;
  }

  .cajupe-product-flow-row {
    gap: 0.55rem;
    padding: 0.45rem 0;
  }

  .cajupe-product-flow-row > span {
    width: 26px;
    height: 26px;
  }

  .cajupe-product-flow-row p strong {
    font-size: 0.68rem;
  }

  .cajupe-product-flow-row em {
    font-size: 0.57rem;
  }

  .cajupe-product-chart {
    height: 68px;
    margin-top: 0.55rem;
  }
}

/*
 * Monitores largos também podem ter pouca altura útil (notebooks, zoom do
 * navegador e barras do sistema). Nessa situação o layout desktop anterior
 * mantinha o hero preso ao viewport e recortava o CTA com overflow: hidden.
 */
@media (min-width: 960px) and (max-height: 760px) {
  .cajupe-product-hero,
  .page-layout-hero--cover {
    height: max(580px, calc(100vh - clamp(1.5rem, 3vw, 3rem)));
    height: max(580px, calc(100dvh - clamp(1.5rem, 3vw, 3rem)));
    min-height: 580px;
  }

  .cajupe-product-hero-container {
    padding: 1.5rem clamp(2rem, 3vw, 2.75rem);
  }

  .cajupe-product-hero-content {
    gap: 0.8rem;
  }

  .cajupe-product-brand {
    gap: 0.62rem;
    padding: 0.55rem 0.8rem;
  }

  .cajupe-product-brand img {
    width: 38px;
    height: 53px;
  }

  .cajupe-product-brand span {
    font-size: 1.55rem;
  }

  .cajupe-product-eyebrow {
    margin-bottom: 0.55rem;
    padding: 0.42rem 0.65rem;
    font-size: 0.66rem;
  }

  .cajupe-product-copy h1 {
    font-size: clamp(2.35rem, 3.3vw, 3.15rem);
    line-height: 0.98;
  }

  .cajupe-product-copy p {
    margin-top: 0.75rem;
    font-size: 0.94rem;
    line-height: 1.45;
  }

  .cajupe-product-access {
    gap: 0.4rem;
  }

  .cajupe-product-cta {
    height: 56px !important;
  }

  .cajupe-product-hero-visual {
    padding-top: 0.5rem !important;
    padding-bottom: 0.5rem !important;
  }

  .cajupe-product-window {
    transform: scale(0.84);
  }

  .page-layout-hero-content--cover {
    padding: clamp(1.5rem, 2.5vw, 2.25rem) !important;
  }

  .page-layout-hero-stack--cover {
    gap: 0.8rem;
  }

  .page-layout-hero--immersive .page-layout-hero-content--cover {
    max-height: calc(100% - 2rem);
  }

  .page-layout-hero--immersive .hero-image--cover {
    width: min(100%, 220px);
    max-height: 130px;
  }

  .page-layout-hero--dark-legacy .hero-image--cover {
    width: min(100%, 125px);
  }

  .page-layout-hero-immersive-promo {
    gap: 0.45rem;
  }

  .page-layout-hero-immersive-promo h3 {
    font-size: clamp(1.55rem, 2vw, 2.1rem);
  }

  .page-layout-hero-immersive-promo p {
    font-size: 0.88rem;
    line-height: 1.4;
  }

  .page-layout-hero--immersive .page-layout-hero-paragraph--cover {
    font-size: 0.84rem !important;
    line-height: 1.4;
  }

  .page-layout-hero-cta--cover {
    height: 48px !important;
  }
}
</style>
